// src/components/dataSells/FieldMappingModal.jsx
import React, { useState, useEffect } from 'react';
import { FaTimes, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { CAMPOS_ESTANDAR, SISTEMAS_ORIGEN, FORMATOS_FECHA, SEPARADORES_DECIMAL } from '../../helpers/dataSellsService';
import useDataSells from '../../hooks/useDataSells';

/**
 * Modal para mapear campos manualmente
 */
const FieldMappingModal = ({ archivo, onClose, mapeoExistente }) => {
    const { obtenerCampos, crearMapeo, actualizarMapeo, validarArchivoSubido, loading } = useDataSells();

    const [camposDisponibles, setCamposDisponibles] = useState([]);
    const [datosPreview, setDatosPreview] = useState([]);
    const [sistemaOrigen, setSistemaOrigen] = useState('otro');
    const [mapeoCampos, setMapeoCampos] = useState({});
    const [configuracionFormato, setConfiguracionFormato] = useState({
        separadorDecimal: '.',
        formatoFecha: 'YYYY-MM-DD'
    });
    const [error, setError] = useState(null);
    const [mapeoOriginal, setMapeoOriginal] = useState({});

    useEffect(() => {
        cargarDatosIniciales();
    }, [archivo]);

    const cargarDatosIniciales = async () => {
        try {
            // PRIMERO: Validar el archivo para obtener el mapeo existente
            let mapeoDelBackend = null;

            try {
                // Llamar en modo silencioso para no mostrar notificaciones
                const resultadoValidacion = await validarArchivoSubido(archivo.id || archivo._id, true);

                if (resultadoValidacion.mapeoExistente) {
                    mapeoDelBackend = resultadoValidacion.mapeoExistente;
                }
            } catch (validacionError) {
                // Continuar sin mapeo si falla la validación
            }

            // Cargar campos disponibles
            const resultado = await obtenerCampos(archivo.id || archivo._id);

            setCamposDisponibles(resultado.headers || []);
            setDatosPreview(resultado.datosPreview || []);

            // Cargar mapeo (prioridad: mapeoDelBackend > prop > archivo)
            const mapeo = mapeoDelBackend || mapeoExistente || archivo.mapeoExistente;

            if (mapeo) {
                if (mapeo.mapeoCampos) {
                    setMapeoCampos(mapeo.mapeoCampos);
                    setMapeoOriginal(mapeo.mapeoCampos);
                }

                if (mapeo.sistemaOrigen) {
                    setSistemaOrigen(mapeo.sistemaOrigen);
                }

                if (mapeo.configuracionFormato) {
                    setConfiguracionFormato(mapeo.configuracionFormato);
                }
            }
        } catch (err) {
            console.error('Error en cargarDatosIniciales:', err);
            setError(err.message);
        }
    };

    const handleMapeoChange = (campoEstandar, campoArchivo) => {
        setMapeoCampos(prev => ({
            ...prev,
            [campoEstandar]: campoArchivo
        }));
    };

    const handleGuardarMapeo = async () => {
        try {
            // Validar que al menos los campos requeridos estén mapeados
            const camposRequeridos = ['idVenta', 'fecha', 'monto'];
            const camposFaltantes = camposRequeridos.filter(campo => !mapeoCampos[campo]);

            if (camposFaltantes.length > 0) {
                alert(`Por favor mapea los siguientes campos requeridos: ${camposFaltantes.join(', ')}`);
                return;
            }

            await crearMapeo(
                archivo.id || archivo._id,
                sistemaOrigen,
                mapeoCampos,
                configuracionFormato
            );

            onClose();
        } catch (err) {
            setError(err.message);
        }
    };

    // Agrupar campos estándar por categoría
    const camposPorCategoria = {
        'Identificación': ['idVenta', 'fecha', 'hora'],
        'Información Financiera': ['monto', 'subtotal', 'impuestos', 'porcentajeImpuestos', 'envio', 'moneda', 'montoConDescuento'],
        'Información de Productos': ['nombreProducto', 'codigoProducto', 'cantidad', 'precio', 'precioSinImpuestos', 'precioConImpuestos', 'totalLinea', 'categoria'],
        'Información de Pago': ['tipoPago', 'metodoPago', 'comision', 'neto'],
        'Información del Cliente': ['nombreCliente', 'email', 'telefono', 'nombreFacturacion', 'nombreEnvio'],
        'Estados': ['estadoFinanciero', 'estadoCumplimiento']
    };

    const camposRequeridos = ['idVenta', 'fecha', 'monto'];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Mapear Campos Manualmente
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Archivo: {archivo.nombre}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <FaTimes className="text-xl" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                            <FaExclamationTriangle className="text-red-500 mt-0.5 mr-2" />
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Configuración */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-gray-900">Configuración</h3>

                            {/* Sistema Origen */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Sistema Origen
                                </label>
                                <select
                                    value={sistemaOrigen}
                                    onChange={(e) => setSistemaOrigen(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {SISTEMAS_ORIGEN.map(sistema => (
                                        <option key={sistema.value} value={sistema.value}>
                                            {sistema.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Formato de Fecha */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Formato de Fecha
                                </label>
                                <select
                                    value={configuracionFormato.formatoFecha}
                                    onChange={(e) => setConfiguracionFormato(prev => ({
                                        ...prev,
                                        formatoFecha: e.target.value
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {FORMATOS_FECHA.map(formato => (
                                        <option key={formato.value} value={formato.value}>
                                            {formato.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Separador Decimal */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Separador Decimal
                                </label>
                                <select
                                    value={configuracionFormato.separadorDecimal}
                                    onChange={(e) => setConfiguracionFormato(prev => ({
                                        ...prev,
                                        separadorDecimal: e.target.value
                                    }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {SEPARADORES_DECIMAL.map(separador => (
                                        <option key={separador.value} value={separador.value}>
                                            {separador.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Preview de datos */}
                            {datosPreview.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                                        Vista previa de datos
                                    </h4>
                                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-48 overflow-auto">
                                        <pre className="text-xs text-gray-600">
                                            {JSON.stringify(datosPreview[0], null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Mapeo de Campos */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-gray-900">
                                    Mapeo de Campos
                                    <span className="text-sm font-normal text-gray-500 ml-2">
                                        (* = requerido)
                                    </span>
                                </h3>
                                <div className="flex gap-3 text-xs">
                                    <span className="flex items-center">
                                        <span className="w-3 h-3 bg-green-500 rounded-full mr-1"></span>
                                        Mapeado
                                    </span>
                                    <span className="flex items-center">
                                        <span className="w-3 h-3 bg-red-500 rounded-full mr-1"></span>
                                        Requerido
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {Object.entries(camposPorCategoria).map(([categoria, campos]) => {
                                    // Filtrar campos: primero los no mapeados, luego los mapeados
                                    const camposNoMapeados = campos.filter(campo => !mapeoCampos[campo]);
                                    const camposMapeados = campos.filter(campo => mapeoCampos[campo]);

                                    // Si no hay campos en esta categoría, no mostrarla
                                    if (campos.length === 0) return null;

                                    return (
                                        <div key={categoria}>
                                            <h4 className="text-sm font-medium text-gray-700 mb-2 pb-1 border-b border-gray-200 flex items-center justify-between">
                                                <span>{categoria}</span>
                                                <span className="text-xs text-gray-500">
                                                    {camposMapeados.length}/{campos.length} mapeados
                                                </span>
                                            </h4>
                                            <div className="space-y-2">
                                                {/* Primero mostrar campos NO mapeados */}
                                                {camposNoMapeados.map(campo => (
                                                    <div key={campo} className={`p-2 rounded border ${camposRequeridos.includes(campo) ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                                                        <label className="flex items-center text-xs font-medium mb-1">
                                                            <span className={`w-2 h-2 rounded-full mr-2 ${camposRequeridos.includes(campo) ? 'bg-red-500' : 'bg-yellow-500'}`}></span>
                                                            {campo}
                                                            {camposRequeridos.includes(campo) && (
                                                                <span className="text-red-500 ml-1">*</span>
                                                            )}
                                                        </label>
                                                        <select
                                                            value={mapeoCampos[campo] || ''}
                                                            onChange={(e) => handleMapeoChange(campo, e.target.value)}
                                                            className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${camposRequeridos.includes(campo) && !mapeoCampos[campo]
                                                                ? 'border-red-300 bg-white'
                                                                : 'border-gray-300 bg-white'
                                                                }`}
                                                        >
                                                            <option value="">-- Seleccionar campo --</option>
                                                            {camposDisponibles.map(campoDisponible => (
                                                                <option key={campoDisponible} value={campoDisponible}>
                                                                    {campoDisponible}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {CAMPOS_ESTANDAR[campo] && (
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                {CAMPOS_ESTANDAR[campo]}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}

                                                {/* Luego mostrar campos YA mapeados (visibles, no colapsados) */}
                                                {camposMapeados.map(campo => (
                                                    <div key={campo} className="p-2 bg-green-50 rounded border border-green-200">
                                                        <label className="flex items-center text-xs font-medium text-green-900 mb-1">
                                                            <FaCheck className="w-3 h-3 text-green-600 mr-2" />
                                                            {campo}
                                                            {camposRequeridos.includes(campo) && (
                                                                <span className="text-green-700 ml-1">*</span>
                                                            )}
                                                        </label>
                                                        <select
                                                            value={mapeoCampos[campo] || ''}
                                                            onChange={(e) => handleMapeoChange(campo, e.target.value)}
                                                            className="w-full px-2 py-1.5 text-sm border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                                        >
                                                            <option value="">-- No mapear --</option>
                                                            {camposDisponibles.map(campoDisponible => (
                                                                <option key={campoDisponible} value={campoDisponible}>
                                                                    {campoDisponible}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {CAMPOS_ESTANDAR[campo] && (
                                                            <p className="text-xs text-green-700 mt-1">
                                                                {CAMPOS_ESTANDAR[campo]}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                    <div className="text-sm text-gray-600">
                        {Object.keys(mapeoCampos).filter(k => mapeoCampos[k]).length} campo(s) mapeado(s)
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleGuardarMapeo}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <FaCheck className="mr-2" />
                                    Guardar Mapeo
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FieldMappingModal;

